import { Button, ButtonGroup, Snackbar, Tooltip, makeStyles } from '@material-ui/core'
import React, { useState } from 'react'
import {
  UnpairDeviceFromBoardsMutation,
  UnpairDeviceFromBoardsMutationVariables
} from './__generated__/UnpairDeviceFromBoardsMutation'
import { RebootDeviceMutation, RebootDeviceMutationVariables } from './__generated__/RebootDeviceMutation'

import { AlertDialog } from '../alert-dialog'
import DescriptionIcon from '@material-ui/icons/Description'
import MuiAlert from '@material-ui/lab/Alert'
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive'
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff'
import OfflineBoltIcon from '@material-ui/icons/OfflineBolt'
import PhonelinkEraseIcon from '@material-ui/icons/PhonelinkErase'
import { gql } from 'apollo-boost'
import { useMutation } from '@apollo/react-hooks'
import { useEnableDeviceAcks, useDisableDeviceAcks } from '../../api/useDeviceAcks'

interface IDeviceActions {
  deviceId: string
  boardId?: string
  requestLogs?: () => void
  firmwareVersion?: string | null
}

interface IResponse {
  severity: 'error' | 'success'
  message: string
}

const useStyle = makeStyles({
  container: {
    marginTop: 32,
    display: 'flex',
    justifyContent: 'center'
  }
})

const REBOOT_DEVICE_MUTATION = gql`
  mutation RebootDeviceMutation($deviceId: String!) {
    rebootDevice(input: { device: $deviceId }) {
      rebootRequested
    }
  }
`

const isRebootSupported = (firmwareVersion?: string | null): boolean => {
  if (!firmwareVersion) return false
  const match = firmwareVersion.match(/v(\d+)\.(\d+)\.(\d+)/)
  if (!match) return false
  const [, major, minor, patch] = match.map(Number)
  return major > 4 || (major === 4 && (minor > 2 || (minor === 2 && patch >= 0)))
}

// Force re-pair before unpair to clear stale pairing cache that can cause
// unpair to silently fail. The backend pairing state can get out of sync,
// and force-pairing refreshes it so the subsequent unpair succeeds reliably.
const FORCE_PAIR_MUTATION = gql`
  mutation ForcePairDeviceWithBoard($boardId: String!, $deviceId: String!) {
    pairDeviceWithBoard(input: { boardId: $boardId, deviceId: $deviceId, force: true }) {
      paired
    }
  }
`

const UNPAIR_DEVICE_MUATION = gql`
  mutation UnpairDeviceFromBoardsMutation($pairedDeviceId: String!) {
    unpairDeviceWithBoard(input: { pairedDeviceId: $pairedDeviceId }) {
      unpaired
    }
  }
`

export const DeviceActions = (props: IDeviceActions) => {
  const [showUnpairPrompt, setShowUnpairPrompt] = useState(false)
  const [showRestartPrompt, setShowRestartPrompt] = useState(false)
  const [showLogsPrompt, setShowLogsPrompt] = useState(false)
  const [showEnableAcksPrompt, setShowEnableAcksPrompt] = useState(false)
  const [showDisableAcksPrompt, setShowDisableAcksPrompt] = useState(false)
  const [response, setResponse] = useState<null | IResponse>(null)
  const [forcePairDevice] = useMutation(FORCE_PAIR_MUTATION)
  const [unpairDevice] = useMutation<UnpairDeviceFromBoardsMutation, UnpairDeviceFromBoardsMutationVariables>(
    UNPAIR_DEVICE_MUATION
  )
  const [rebootDevice] = useMutation<RebootDeviceMutation, RebootDeviceMutationVariables>(REBOOT_DEVICE_MUTATION)
  const [enableAcks] = useEnableDeviceAcks()
  const [disableAcks] = useDisableDeviceAcks()
  const rebootSupported = isRebootSupported(props.firmwareVersion)
  const classes = useStyle()

  const actions = [
    {
      open: showUnpairPrompt,
      description: 'Are you sure you want to unpair this device from all boards?',
      onCancel: () => {
        setShowUnpairPrompt(false)
      },
      onConfirm: async () => {
        setShowUnpairPrompt(false)

        try {
          // Force re-pair before unpair to clear stale pairing cache.
          // Without this, unpair can silently fail due to out-of-sync state.
          // Non-blocking — if force-pair fails, still attempt the unpair.
          if (props.boardId) {
            try {
              await forcePairDevice({
                variables: {
                  boardId: props.boardId,
                  deviceId: props.deviceId
                }
              })
            } catch (e) {
              // Best-effort cache refresh, continue with unpair
            }
          }

          await unpairDevice({
            variables: {
              pairedDeviceId: props.deviceId
            }
          })

          setResponse({
            severity: 'success',
            message: 'This device has been unpaired from all boards'
          })
        } catch (err) {
          setResponse({
            severity: 'error',
            message: 'There was an error unpairing this device'
          })
        }
      },
      onShow: () => {
        setShowUnpairPrompt(true)
      },
      icon: <PhonelinkEraseIcon />,
      buttonText: 'Unpair from all boards'
    },
    {
      open: showRestartPrompt,
      description: rebootSupported
        ? 'Are you sure you want to restart this device?'
        : 'Remote reboot is only available for devices on firmware version v4.2.0 or later. Please upgrade the device firmware to use this feature.',
      onCancel: () => {
        setShowRestartPrompt(false)
      },
      onConfirm: async () => {
        setShowRestartPrompt(false)

        if (!rebootSupported) return

        try {
          const result = await rebootDevice({
            variables: {
              deviceId: props.deviceId
            }
          })

          if (result.data?.rebootDevice.rebootRequested) {
            setResponse({
              severity: 'success',
              message: 'Reboot command has been sent to the device'
            })
          } else {
            setResponse({
              severity: 'error',
              message: 'Reboot request was not accepted'
            })
          }
        } catch (err) {
          setResponse({
            severity: 'error',
            message: 'There was an error requesting a reboot for this device'
          })
        }
      },
      onShow: () => {
        setShowRestartPrompt(true)
      },
      icon: <OfflineBoltIcon />,
      buttonText: 'Remotely restart'
    },
    {
      open: showLogsPrompt,
      description: 'Are you sure you want to fetch the remote logs for this device?',
      onCancel: () => {
        setShowLogsPrompt(false)
      },
      onConfirm: async () => {
        setShowLogsPrompt(false)
        if (props.requestLogs) {
          await props.requestLogs()
        }
      },
      onShow: () => {
        setShowLogsPrompt(true)
      },
      icon: <DescriptionIcon />,
      buttonText: 'Fetch verbose logs'
    },
    {
      open: showEnableAcksPrompt,
      description: 'Are you sure you want to enable acks for this device?',
      onCancel: () => {
        setShowEnableAcksPrompt(false)
      },
      onConfirm: async () => {
        setShowEnableAcksPrompt(false)

        try {
          await enableAcks({ variables: { device: props.deviceId } })
          setResponse({
            severity: 'success',
            message: 'Acks have been enabled for this device'
          })
        } catch (err) {
          setResponse({
            severity: 'error',
            message: 'There was an error enabling acks for this device'
          })
        }
      },
      onShow: () => {
        setShowEnableAcksPrompt(true)
      },
      icon: <NotificationsActiveIcon />,
      buttonText: 'Enable Acks',
      tooltip: 'The ack state is managed on device'
    },
    {
      open: showDisableAcksPrompt,
      description: 'Are you sure you want to disable acks for this device?',
      onCancel: () => {
        setShowDisableAcksPrompt(false)
      },
      onConfirm: async () => {
        setShowDisableAcksPrompt(false)

        try {
          await disableAcks({ variables: { device: props.deviceId } })
          setResponse({
            severity: 'success',
            message: 'Acks have been disabled for this device'
          })
        } catch (err) {
          setResponse({
            severity: 'error',
            message: 'There was an error disabling acks for this device'
          })
        }
      },
      onShow: () => {
        setShowDisableAcksPrompt(true)
      },
      icon: <NotificationsOffIcon />,
      buttonText: 'Disable Acks',
      tooltip: 'The ack state is managed on device'
    }
  ]

  const closeResponse = () => setResponse(null)

  return (
    <div className={classes.container}>
      <Snackbar open={!!response} autoHideDuration={6000} onClose={closeResponse}>
        <MuiAlert onClose={closeResponse} severity={response?.severity}>
          {response?.message}
        </MuiAlert>
      </Snackbar>
      {actions.map((dialog, key) => (
        <AlertDialog
          key={key}
          open={dialog.open}
          title='Please Confirm'
          description={dialog.description}
          onCancel={dialog.onCancel}
          onConfirm={dialog.onConfirm}
        />
      ))}
      <ButtonGroup
        orientation='horizontal'
        color='default'
        aria-label='vertical outlined primary button group'
        variant='text'>
        {actions.map((action, key) => {
          const button = (
            <Button size='medium' startIcon={action.icon} onClick={action.onShow} key={key}>
              {action.buttonText}
            </Button>
          )
          return action.tooltip ? (
            <Tooltip key={key} title={action.tooltip} arrow>
              {button}
            </Tooltip>
          ) : (
            button
          )
        })}
      </ButtonGroup>
    </div>
  )
}
